import { useState, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Quote } from "lucide-react";

const intro =
  "Several historical statements from Catholic writers, clergy, and other prominent Christian figures explicitly acknowledge that Sunday observance was not established by a direct biblical command — and that ecclesiastical authority played a role in the transition from Saturday to Sunday. (Note: Constantine's 321 AD law made Sunday a civil day of rest; the development of Sunday as a Christian day of worship involved a longer process of church practice and later ecclesiastical legislation.)";

const quotes = [
  {
    who: "James Cardinal Gibbons",
    src: "The Catholic Mirror, September 23, 1893",
    lines: ["\u201CThe Catholic Church, \u2026 by virtue of her divine mission, changed the day from Saturday to Sunday.\u201D"],
  },
  {
    who: "Stephen Keenan",
    src: "A Doctrinal Catechism",
    lines: [
      "\u201CShe could not have substituted the observance of Sunday \u2026 for the observance of Saturday \u2026 [without] power.\u201D",
      "\u2026described as \u201Ca change for which there is no Scriptural authority.\u201D",
    ],
  },
  {
    who: "C. F. Thomas — Chancellor to Cardinal Gibbons",
    src: "Letter, 1895",
    lines: [
      "\u201COf course the Catholic Church claims that the change was her act.\u201D",
      "\u2026\u201Ca mark of her ecclesiastical power and authority in religious matters.\u201D",
    ],
  },
  {
    who: "The Catholic Record",
    src: "London, Ontario, September 1, 1923",
    lines: ["\u201CSunday is our mark of authority. \u2026 The church is above the Bible, and this transference of Sabbath observance is proof of that fact.\u201D"],
  },
  {
    who: "Monsignor Louis Gaston de S\u00E9gur",
    src: "Plain Talk About the Protestantism of Today, 1868, p. 213",
    lines: ["\u201CThe observance of Sunday by the Protestants is an homage they pay, in spite of themselves, to the authority of the Catholic Church.\u201D"],
  },
  {
    who: "Our Sunday Visitor",
    src: "February 5, 1950",
    lines: ["\u201CIn observing Sunday, [Protestants] are accepting the authority of the spokesman for the Church, the pope.\u201D"],
  },
  {
    who: "Peter Geiermann, C.SS.R.",
    src: "The Convert's Catechism of Catholic Doctrine",
    lines: [
      "Q: \u201CWhich is the Sabbath day?\u201D  A: \u201CSaturday is the Sabbath day.\u201D",
      "Q: \u201CWhy do we observe Sunday instead of Saturday?\u201D  A: \u201CBecause the Catholic Church transferred the solemnity from Saturday to Sunday.\u201D",
    ],
  },
  {
    who: "Catholic Virginian",
    src: "1947",
    lines: ["\u201CNowhere in the Bible do we find that Christ or the Apostles ordered that the Sabbath be changed from Saturday to Sunday.\u201D \u2014 explaining Sunday is observed because the change was revealed through the Catholic Church \u201Coutside the Bible.\u201D"],
  },
  {
    who: "John O'Brien",
    src: "Faith of Millions",
    lines: ["\u201CSince Saturday, not Sunday, is specified in the Bible\u2026\u201D the Sunday practice rests upon \u201Cthe authority of the Catholic Church and not upon an explicit text in the Bible.\u201D"],
  },
  {
    who: "Clovis G. Chappell — Methodist",
    src: "Ten Rules for Living",
    lines: [
      "\u201COne will search the Scriptures in vain for authority for changing from the seventh day to the first.\u201D",
      "\u2026the Christian Sabbath is \u201Cnot a matter of positive command. It is a gift of the church.\u201D",
    ],
  },
];

export const SabbathQuotes = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-sabbath-quotes", handler);
    return () => window.removeEventListener("open-sabbath-quotes", handler);
  }, []);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="sabbath-quotes-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-3xl max-h-[88vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 border border-[#4A453F] bg-[#141311] p-7 md:p-10 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Close
            data-testid="sabbath-quotes-close"
            className="absolute top-5 right-5 z-10 text-[#A39E98] hover:text-[#F4F0EA] transition-colors"
          >
            <X strokeWidth={1} className="h-6 w-6" />
          </DialogPrimitive.Close>

          <div className="flex items-center gap-2 mb-4">
            <Quote strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
              In Their Own Words
            </span>
          </div>
          <DialogPrimitive.Title className="font-heading font-light tracking-tight text-3xl md:text-4xl leading-tight text-[#F4F0EA] mb-5">
            Famous Quotes About the <span className="italic text-[#D4AF37]">Sabbath Change</span>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm md:text-base leading-relaxed font-light text-[#A39E98] mb-8">
            {intro}
          </DialogPrimitive.Description>

          <div className="space-y-4">
            {quotes.map((q, i) => (
              <div key={q.who} data-testid={`sabbath-quote-${i}`} className="border border-[#2B2825] bg-[#0A0A0A] p-6">
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="font-heading text-2xl text-[#D4AF37]/70 leading-none">{i + 1}</span>
                  <div>
                    <p className="font-heading text-lg md:text-xl font-light text-[#F4F0EA] leading-snug">{q.who}</p>
                    <p className="font-mono text-xs tracking-wide text-[#D4AF37] mt-1">{q.src}</p>
                  </div>
                </div>
                <div className="space-y-3 pl-1 border-l-2 border-[#D4AF37]/40">
                  {q.lines.map((line, j) => (
                    <p key={j} className="pl-4 text-sm md:text-base leading-relaxed font-light italic text-[#F4F0EA]/90">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 font-mono text-xs text-[#A39E98]/70 leading-relaxed">
            Quotations are presented for study. When citing publicly, display the publication, date, and quotation exactly as sourced.
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export const openSabbathQuotes = () => {
  window.dispatchEvent(new Event("open-sabbath-quotes"));
  if (typeof window.gtag === "function") window.gtag("event", "open_sabbath_quotes", { category: "engagement" });
};
